// Accurate color name → CSS hex mapping for Doe Wiz swatches.
// Supports compound names like "Black/White" and "Forest Green" by rendering split
// linear-gradient backgrounds. Unknown names fall back to a neutral dashed chip.

const COLOR_HEX = {
  // Neutrals
  black: '#0A0A0A',
  white: '#FFFFFF',
  cream: '#F5EEDC',
  ivory: '#F8F4E3',
  grey: '#8A8A8A',
  gray: '#8A8A8A',
  'light grey': '#CCCCCC',
  'dark grey': '#4A4A4A',
  silver: '#C0C0C0',

  // Browns / Tans
  brown: '#5C3A21',
  tan: '#C8A165',
  wheat: '#D4A373',
  khaki: '#B8A67A',
  mocha: '#6F4E37',
  coffee: '#4B3621',

  // Reds / Pinks
  red: '#D32027',
  'dark red': '#8B1A1F',
  burgundy: '#6B1028',
  maroon: '#7A1F2B',
  coral: '#FF7F5C',
  pink: '#F4C1CC',
  'hot pink': '#E6266D',
  blush: '#F6CBC7',
  salmon: '#FA8072',

  // Blues
  blue: '#1E5FB3',
  'royal blue': '#1B3FA0',
  navy: '#0E1E40',
  'light blue': '#7FB3E6',
  denim: '#3C5A80',
  'light wash': '#8CA8C8',
  teal: '#0E8F8F',

  // Greens
  green: '#2E7D32',
  'forest green': '#0F3D1F',
  'dark green': '#13331A',
  olive: '#6B6B2E',
  mint: '#A8E6CF',
  lime: '#B8DD2E',

  // Yellows / Oranges
  yellow: '#F5C518',
  gold: '#D4AF37',
  orange: '#EB6B1E',
  'burnt orange': '#B84818',

  // Purples
  purple: '#5E2CA5',
  violet: '#7A3EBD',
  lavender: '#C8B3E8',
};

const normalise = (s = '') => s.trim().toLowerCase().replace(/\s+/g, ' ');

// Resolve a single color token to a hex value (e.g., "Royal Blue" → #1B3FA0)
function resolveHex(token) {
  const n = normalise(token);
  if (COLOR_HEX[n]) return COLOR_HEX[n];
  // Strip common descriptors and retry
  const stripped = n.replace(/\b(stripe|striped|pattern|mix|mixed)\b/g, '').trim();
  if (COLOR_HEX[stripped]) return COLOR_HEX[stripped];
  // Try last word (e.g., "Burnt Sunrise" → "sunrise" → unknown, fall back)
  const lastWord = stripped.split(' ').slice(-1)[0];
  if (COLOR_HEX[lastWord]) return COLOR_HEX[lastWord];
  return null;
}

// Return an inline style object for a swatch given a color name.
// - Splits compound names on "/" or "&" into a split linear-gradient.
// - Multi-token single-color names (e.g. "Forest Green") resolve as a solid.
// - Unknown colors return a subtle neutral chip so we never render a wrong hue.
export function getSwatchStyle(colorName) {
  if (!colorName) return { backgroundColor: '#E5E5E5', backgroundImage: 'repeating-linear-gradient(45deg,#D4D4D4 0 2px,transparent 2px 4px)' };
  const parts = colorName.split(/[\/&]/).map(s => s.trim()).filter(Boolean);
  const hexes = parts.map(resolveHex);

  // All tokens resolved – render solid (1 part) or split gradient (2+ parts)
  if (hexes.every(Boolean)) {
    if (hexes.length === 1) return { backgroundColor: hexes[0] };
    if (hexes.length === 2) {
      return { backgroundImage: `linear-gradient(135deg, ${hexes[0]} 0 50%, ${hexes[1]} 50% 100%)` };
    }
    // 3+ parts: equal stripes
    const stops = hexes.map((h, i) => `${h} ${(i * 100) / hexes.length}% ${((i + 1) * 100) / hexes.length}%`).join(',');
    return { backgroundImage: `linear-gradient(135deg, ${stops})` };
  }

  // If at least the first part resolves, use that as the primary hue
  if (hexes[0]) return { backgroundColor: hexes[0] };

  // Unknown color – neutral dashed chip
  return {
    backgroundColor: '#F5F5F5',
    backgroundImage: 'repeating-linear-gradient(45deg,#D4D4D4 0 2px,transparent 2px 4px)',
  };
}
